import { useEffect, useState } from 'react'
import clsx from 'clsx'
import DayOfWeekBars from '@/components/DayOfWeekBars'
import { dayOfWeekAverages, type DayOfWeekPoint } from '@/db/queries'

const RANGES = [
  { days: 90, label: '90D' },
  { days: 365, label: '1Y' },
  { days: 1825, label: '5Y' },
]

export default function DayOfWeek() {
  const [days, setDays] = useState(365)
  const [data, setData] = useState<DayOfWeekPoint[]>([])

  useEffect(() => {
    dayOfWeekAverages(days).then(setData)
  }, [days])

  // Reorder so Mon..Sun feels natural.
  const ordered = [...data.slice(1), data[0]].filter(Boolean)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Day of week</h1>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={clsx(
                'px-3 py-1 text-sm rounded',
                days === r.days
                  ? 'bg-brand/25 text-brand-fg font-medium'
                  : 'text-stone-600 hover:bg-stone-100',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Recovery">
          <DayOfWeekBars
            data={ordered}
            metricKey="recovery"
            color="#16a34a"
            domain={[0, 100]}
            format={(v) => `${Math.round(v)}%`}
          />
        </Card>
        <Card title="Strain">
          <DayOfWeekBars
            data={ordered}
            metricKey="strain"
            color="#0ea5e9"
            domain={[0, 21]}
            format={(v) => v.toFixed(1)}
          />
        </Card>
        <Card title="HRV">
          <DayOfWeekBars
            data={ordered}
            metricKey="hrv"
            color="#a855f7"
            format={(v) => `${Math.round(v)} ms`}
          />
        </Card>
        <Card title="Sleep">
          <DayOfWeekBars
            data={ordered}
            metricKey="sleep_minutes"
            color="#6366f1"
            format={formatHm}
          />
        </Card>
      </div>
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-stone-500 mb-4">{title}</div>
      {children}
    </div>
  )
}

function formatHm(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  const h = Math.floor(m / 60)
  const mm = m % 60
  if (h === 0) return `${mm}m`
  if (mm === 0) return `${h}h`
  return `${h}h ${mm}m`
}
